import { CartLinesUpdateEvent } from '@shopify/events';

const clTrack = (name, data) => {
  try {
    // @ts-ignore Shopify analytics global; custom events reach Customer Events pixels (Meta, Clarity…)
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {}
};

/**
 * Cart extras: "Add" on the Complete-your-look charm. Adds through the AJAX cart and lets the
 * cart (drawer or page) re-render from the returned sections, like every other add on the site.
 * Also used as <cl-quick-add> (homepage "Just dropped" row, sections/cl-themes.liquid):
 * data-context / data-track name the add, the "+" shows a tick once added, and
 * [data-scroll] buttons move the [data-rail] row sideways.
 * A button with data-variant-id="1,2,3" adds several variants at once (Claspie trios, sections/cl-trios.liquid).
 */
class ClaspLabCartExtras extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // Swipe track under each sideways row (homepage Claspies on phones, Cris 3 Nov): the dark part shows how much of
    // the row is in view and where, so it's clear there is more to the right
    this.#tracks = [...this.querySelectorAll('[data-rail]')]
      .map((rail) => ({ rail, fill: rail.nextElementSibling?.querySelector('.cl-themes__swipe-fill') }))
      .filter((t) => t.rail instanceof HTMLElement && t.fill instanceof HTMLElement);
    this.#tracks.forEach((t) => t.rail.addEventListener('scroll', this.#onRailScroll, { passive: true }));
    if (this.#tracks.length) {
      window.addEventListener('resize', this.#onRailScroll, { passive: true });
      this.#onRailScroll();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#tracks.forEach((t) => t.rail.removeEventListener('scroll', this.#onRailScroll));
    window.removeEventListener('resize', this.#onRailScroll);
  }

  /** @type {{ rail: HTMLElement, fill: HTMLElement }[]} */
  #tracks = [];

  #onRailScroll = () => {
    for (const { rail, fill } of this.#tracks) {
      const size = Math.min(100, (rail.clientWidth / Math.max(rail.scrollWidth, 1)) * 100);
      const room = rail.scrollWidth - rail.clientWidth;
      const share = room > 0 ? rail.scrollLeft / room : 0;
      fill.style.setProperty('--cl-swipe-size', `${size}%`);
      // translateX is relative to the fill's own width: move it across the free part of the track
      fill.style.setProperty('--cl-swipe-x', `${size > 0 ? (share * (100 - size) * 100) / size : 0}%`);
    }
  };

  /** @param {Event} event */
  #onClick = async (event) => {
    const arrow = event.target instanceof Element ? event.target.closest('[data-scroll]') : null;
    if (arrow instanceof HTMLElement) {
      const rail = this.querySelector('[data-rail]');
      if (rail instanceof HTMLElement) rail.scrollBy({ left: Number(arrow.dataset.scroll) * rail.clientWidth * 0.8 });
      return;
    }
    const button = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;
    const ids = String(button.dataset.variantId || '')
      .split(',')
      .map(Number)
      .filter(Boolean);
    const id = ids[0];
    if (!id) return;
    // @ts-ignore set by snippets/cl-preorder.liquid when pre-orders are on
    const preorder = window.ClaspLabPreorder;
    const line = (/** @type {any} */ item) => (preorder ? preorder.line(item, button.dataset.preorder === 'true') : item);
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: this.dataset.context || 'cart',
        lines: ids.map((v) => ({ merchandiseId: String(v), quantity: 1 })),
        promise: deferred.promise,
      })
    );
    try {
      // @ts-ignore Theme is a global set by the theme's scripts snippet
      const routes = Theme.routes;
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((v) => line({ id: v, quantity: 1 })), sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (!response.status) clTrack(this.dataset.track || 'cart_upsell_added', { variant_id: id });
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: this.localName,
          sourceId: this.localName,
          itemCount: ids.length,
          sections: response.sections,
          didError: Boolean(response.status),
        },
      });
      // Outside the cart (which re-renders) the button stays: show a tick for a moment
      if (this.localName !== 'cl-cart-extras') {
        button.removeAttribute('aria-busy');
        if (!response.status) {
          button.dataset.added = 'true';
          setTimeout(() => delete button.dataset.added, 2200);
        }
      }
    } catch (e) {
      console.error(e);
      deferred.reject(e);
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('cl-cart-extras')) customElements.define('cl-cart-extras', ClaspLabCartExtras);
if (!customElements.get('cl-quick-add')) customElements.define('cl-quick-add', class extends ClaspLabCartExtras {});

/**
 * Sale countdown in the cart (theme settings → Claspora → Cart). Counts down to a real sale end and
 * removes itself when the sale is over. One ticker for the page, because the cart re-renders.
 */
const clSaleTick = () => {
  const els = document.querySelectorAll('[data-cl-sale-end]');
  if (!els.length) return;
  const now = Date.now() / 1000;
  els.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const left = Math.floor(Number(el.dataset.clSaleEnd) - now);
    if (!(left > 0)) {
      el.hidden = true;
      return;
    }
    const d = Math.floor(left / 86400);
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
    const hms = `${pad(Math.floor((left % 86400) / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
    const out = el.querySelector('[data-cl-sale-time]');
    if (out) out.textContent = d > 0 ? `${d}d ${hms}` : hms;
  });
};
clSaleTick();
setInterval(clSaleTick, 1000);

/**
 * Euro amounts in the visitor's currency (Cris, 3 Nov; snippets/cl-shipping.liquid). Shipping rates and the free-shipping
 * threshold are euros in Shopify; Markets converts them with window.Shopify.currency.rate. Checked 3 Nov on the checkout's
 * rates (cart/shipping_rates.json): the converted rate is rounded UP to whole units, HUF to hundreds
 * (SEK €7.90 → 89 kr, €3.50 → 40 kr; CHF €14.90 → 14.00, €7.90 → 8.00; DKK 61 / 27; PLN 36 / 16; CZK 197 / 87;
 * HUF 3 000 / 1 400). The threshold is rounded too, but not up (CHF: €100 = 93.45, free from 93.00), so rounding it up
 * means the theme never says "free" before the checkout does; at worst it asks for 1 unit more.
 * Only those currencies convert. Anything else (RON showed 43.53 lei for €7.90: not a plain conversion) gives 0,
 * and every hint built on it stays hidden or keeps "calculated at checkout".
 */
/** @type {Record<string, number>} rounding step in cents */
const FX_STEP = { SEK: 100, DKK: 100, PLN: 100, CZK: 100, CHF: 100, HUF: 10000 };
/**
 * Prices and fixed € discounts (Cris, 3 Nov): checked on the dev store's /cart.js. Product prices = euro × rate rounded
 * UP to the same steps (CHF €18 → 17, €29.99 → 29; SEK €18 → 202; HUF €18 → 6 700; RON €29.99 → 164); € off per item =
 * euro × rate rounded to the nearest cent (CHF €1 → 0.93, €5 → 4.67, €8.25 → 7.71; DKK €5 → 38.12; SEK €8.25 → 92.16;
 * PLN €5 → 22.55; CZK €1 → 24.83; RON €5 → 27.25; HUF €5 → 1 859.05). RON prices convert like this, only its shipping
 * doesn't. Unknown currency → NaN, and the amount stays hidden.
 */
/** @type {Record<string, number>} */
const PRICE_STEP = { ...FX_STEP, RON: 100 };
const fxNow = () => {
  // @ts-ignore Shopify storefront global
  const currency = window.Shopify?.currency;
  return { active: currency?.active || '', rate: Number(currency?.rate) };
};
const ClaspLabFx = {
  /** true when euro prices and discounts can be shown in this currency */
  known() {
    const { active, rate } = fxNow();
    return (active === 'EUR' && rate === 1) || (Boolean(PRICE_STEP[active]) && rate > 0);
  },
  /** A euro product price (e.g. the standard Claspie €18) as Shopify shows it here @param {number} eurCents */
  price(eurCents) {
    const { active, rate } = fxNow();
    if (active === 'EUR' && rate === 1) return eurCents;
    const step = PRICE_STEP[active];
    if (!step || !(rate > 0)) return NaN;
    return Math.ceil((eurCents * rate) / step - 1e-9) * step;
  },
  /** A fixed € discount per item (packs, promotion, band deal) as Shopify applies it here @param {number} eurCents */
  off(eurCents) {
    const { active, rate } = fxNow();
    if (active === 'EUR' && rate === 1) return eurCents;
    if (!PRICE_STEP[active] || !(rate > 0)) return NaN;
    return Math.round(eurCents * rate);
  },
  /** @param {number} eurCents @returns {number} cents in the visitor's currency, 0 = unknown */
  toLocal(eurCents) {
    // @ts-ignore Shopify storefront global
    const currency = window.Shopify?.currency;
    const active = currency?.active;
    const rate = Number(currency?.rate);
    if (!(eurCents > 0) || !active) return 0;
    if (active === 'EUR' && rate === 1) return eurCents;
    const step = FX_STEP[active];
    if (!step || !(rate > 0)) return 0;
    return Math.ceil((eurCents * rate) / step - 1e-9) * step;
  },
  /**
   * Like Liquid's money_without_trailing_zeros (or money with keepZeros) in the shop's money format
   * @param {number} cents @param {string} format @param {boolean} [keepZeros]
   */
  money(cents, format, keepZeros = false) {
    const value = cents / 100;
    const [int, dec] = value.toFixed(2).split('.');
    const group = (/** @type {string} */ s, /** @type {string} */ sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const whole = Math.round(value).toString();
    /** @type {Record<string, string>} */
    const map = {
      amount: `${group(int, ',')}.${dec}`,
      amount_no_decimals: group(whole, ','),
      amount_with_comma_separator: `${group(int, '.')},${dec}`,
      amount_no_decimals_with_comma_separator: group(whole, '.'),
      amount_with_space_separator: `${group(int, ' ')},${dec}`,
      amount_no_decimals_with_space_separator: group(whole, ' '),
      amount_with_apostrophe_separator: `${group(int, "'")}.${dec}`,
    };
    const out = (format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => map[key] ?? map.amount);
    return cents % 100 === 0 && !keepZeros ? out.replace(/([.,])00(?!\d)/, '') : out;
  },
  /**
   * A deal formula from snippets/cl-fx-money.liquid in the visitor's currency, in cents (NaN = can't convert):
   * "L|P|n:O,n:O" = L + P × standard Claspie price − n × € off per item; ";" = the lowest of several.
   * @param {string} formula @param {number} baseEur standard Claspie price, euro cents
   */
  formula(formula, baseEur) {
    const values = String(formula || '')
      .split(';')
      .filter(Boolean)
      .map((alt) => {
        const [local = '0', count = '0', offs = ''] = alt.split('|');
        let value = Number(local) + (Number(count) ? Number(count) * ClaspLabFx.price(baseEur) : 0);
        for (const term of offs.split(',').filter(Boolean)) {
          const [n, eur] = term.split(':').map(Number);
          if (n) value -= n * ClaspLabFx.off(eur);
        }
        return value;
      });
    return values.length ? Math.min(...values) : NaN;
  },
};
// @ts-ignore used by assets/clasplab-bundle.js (buy-box free-shipping hint)
window.ClaspLabFx = ClaspLabFx;

/**
 * <cl-ship-fx> (snippets/cl-shipping.liquid 'cart_line', snippets/cl-cart-extras.liquid): the cart's shipping line
 * (data-mode="line") and free-shipping bar (data-mode="bar") outside euros. The cart re-renders by morphing, which
 * puts the server's fallback back, so a MutationObserver fills it in again; writes only happen when something differs.
 */
class ClaspLabShipFx extends HTMLElement {
  /** @type {MutationObserver | null} */ #observer = null;

  connectedCallback() {
    this.#render();
    this.#observer = new MutationObserver(() => this.#render());
    this.#observer.observe(this, { attributes: true, childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #render() {
    const d = this.dataset;
    const free = ClaspLabFx.toLocal(Number(d.freeEur || 0));
    const total = Number(d.total || 0);
    const word = d.word || '';
    const money = (/** @type {number} */ cents) => ClaspLabFx.money(cents, d.moneyFormat || '');

    if (d.mode === 'line') {
      const rate = ClaspLabFx.toLocal(Number(d.rateEur || 0));
      if (!free || !rate) return; // the fallback ("Shipping is calculated at checkout.") stays
      const ship = total >= free ? `Free ${word}shipping.` : `Shipping to ${d.country}: ${money(rate)}.`;
      const text = [d.tax, ship].filter(Boolean).join(' ');
      const out = this.querySelector('[data-fx-out]');
      if (out && out.textContent === text && this.childElementCount === 1) return;
      const small = document.createElement('small');
      small.dataset.fxOut = '';
      small.textContent = text;
      this.replaceChildren(small);
      return;
    }

    // bar
    if (!free) {
      if (!this.hidden) this.hidden = true;
      return;
    }
    const left = free - total;
    const card = this.querySelector('[data-fx-card]');
    const short = this.querySelector('[data-fx-short]');
    const long = this.querySelector('[data-fx-text]');
    const fill = this.querySelector('[data-fx-fill]');
    if (!card || !short || !long || !(fill instanceof HTMLElement)) return;
    const strong = (/** @type {string} */ s) => Object.assign(document.createElement('strong'), { textContent: s });
    if (left > 0) {
      const amount = money(left);
      if (short.textContent !== `${amount} to free ${word}shipping`) {
        short.replaceChildren(strong(amount), ` to free ${word}shipping`);
      }
      if (long.textContent !== `You're ${amount} away from free ${word}shipping.`) {
        long.replaceChildren("You're ", strong(amount), ` away from free ${word}shipping.`);
      }
    } else {
      if (short.textContent) short.replaceChildren();
      if (long.textContent !== `Free ${word}shipping on this order.`) {
        long.replaceChildren(strong(`Free ${word}shipping`), ' on this order.');
      }
    }
    // Phones: the one-line version while there's something left, like the euro bar (variant C, Cris 22 Oct)
    card.classList.toggle('cl-cart-x__ship--compact', left > 0);
    const width = `${Math.min(100, Math.max(0, (total / free) * 100)).toFixed(1)}%`;
    if (fill.style.width !== width) fill.style.width = width;
    if (this.hidden) this.hidden = false;
  }
}

if (!customElements.get('cl-ship-fx')) customElements.define('cl-ship-fx', ClaspLabShipFx);

/**
 * <cl-fx-money> (snippets/cl-fx-money.liquid): a deal price outside euros, worked out from its formula. Re-applies after
 * the cart morphs (same pattern as cl-ship-fx); unknown currency → data-tpl-none or nothing.
 */
class ClaspLabFxMoney extends HTMLElement {
  /** @type {MutationObserver | null} */ #observer = null;

  connectedCallback() {
    this.#render();
    this.#observer = new MutationObserver(() => this.#render());
    this.#observer.observe(this, { attributes: true, childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #render() {
    const d = this.dataset;
    const base = Number(d.base || 1800);
    let value = ClaspLabFx.formula(d.f || '', base);
    if (d.minus) value -= ClaspLabFx.formula(d.minus, base);
    if (Number(d.div) > 0) value = Math.floor(value / Number(d.div));
    let text = d.tplNone || '';
    if (Number.isFinite(value)) {
      const amount = ClaspLabFx.money(value, d.moneyFormat || '', 'zeros' in d);
      text = d.tplFree && value <= 0 ? d.tplFree : d.tpl ? d.tpl.replace('%a', amount) : amount;
    }
    if (this.textContent !== text) this.textContent = text;
  }
}

if (!customElements.get('cl-fx-money')) customElements.define('cl-fx-money', ClaspLabFxMoney);
